import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { AlertIcon, CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon } from '../components/icons'
import {
  buildMonthGrid,
  canGoToNextMonth,
  canGoToPreviousMonth,
  findKeyboardTarget,
  fromLocalDateString,
  isBookable,
  isCalendarNavigationKey,
  toWeeks,
  WEEKDAY_LABELS,
  WEEKDAY_NAMES,
  type CalendarCell,
} from '../lib/calendar'
import { formatDayLabel, formatMonthLabel, formatSlotTime, isSameDay, toLocalDateString } from '../lib/format'
import type {
  AvailableJobTimeSlotDTO,
  DayCapacity,
  LocalDateString,
} from '../types/PublicBookingChannelTypes'

export interface ScheduleStepProps {
  monthCursor: Date
  onMonthChange: (cursor: Date) => void
  minDate: Date
  maxDate: Date
  capacityByDate: Record<LocalDateString, DayCapacity>
  monthLoading: boolean
  selectedDate: Date | null
  onSelectDate: (date: Date) => void
  slots: AvailableJobTimeSlotDTO[]
  slotsLoading: boolean
  slotsError: string | null
  onRetrySlots: () => void
  selectedSlot: AvailableJobTimeSlotDTO | null
  onSelectSlot: (slot: AvailableJobTimeSlotDTO) => void
}

const CAPACITY_LABELS: Record<DayCapacity, string> = {
  open: 'good availability',
  limited: 'limited availability',
  full: 'fully booked',
  closed: 'closed',
}

/** Keys that move between times: the list reads left to right, then down. */
const SLOT_KEYS: Record<string, number | 'first' | 'last'> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last',
}

export function ScheduleStep({
  monthCursor,
  onMonthChange,
  minDate,
  maxDate,
  capacityByDate,
  monthLoading,
  selectedDate,
  onSelectDate,
  slots,
  slotsLoading,
  slotsError,
  onRetrySlots,
  selectedSlot,
  onSelectSlot,
}: ScheduleStepProps) {
  const cells = buildMonthGrid(monthCursor, minDate, maxDate)
  const canGoBack = canGoToPreviousMonth(monthCursor, minDate)
  const canGoForward = canGoToNextMonth(monthCursor, maxDate)
  const monthLabelId = useId()

  const isUnavailable = (cell: CalendarCell) => cell.outOfRange || !isBookable(capacityByDate[cell.key])
  const dayIsOpen = (date: Date) => isBookable(capacityByDate[toLocalDateString(date)])

  /**
   * One tab stop for the whole month (roving tabindex): Tab enters the grid
   * once and the arrow keys do the rest. A keyboard customer used to Tab
   * through every open day, fifteen presses to reach a date 17 days out.
   *
   * The stop is the open day last focused, then the chosen day, then the
   * first open day, so tabbing back in lands where the customer left off. A
   * day focused before the month's availability arrived can turn out to be
   * closed; it keeps focus if it has it, but stops being the way in.
   */
  const [focusedKey, setFocusedKey] = useState<string | null>(null)
  const gridRef = useRef<HTMLDivElement | null>(null)
  const pendingFocus = useRef<string | null>(null)
  const daypickRef = useRef<HTMLButtonElement | null>(null)
  const focusDaypick = useRef(false)

  const daysInMonth = cells.filter((cell) => cell.inMonth)
  const openKeys = new Set(daysInMonth.filter((cell) => !isUnavailable(cell)).map((cell) => cell.key))
  const selectedKey = selectedDate ? toLocalDateString(selectedDate) : null
  const tabStopKey =
    (focusedKey && openKeys.has(focusedKey) ? focusedKey : null) ??
    (selectedKey && openKeys.has(selectedKey) ? selectedKey : null) ??
    openKeys.values().next().value ??
    daysInMonth[0]?.key ??
    null

  /**
   * Moves focus once the day it is headed for is on screen. A key that crosses
   * into another month changes the month first, so the day only exists after
   * the next render; running after every render covers both cases.
   */
  useEffect(() => {
    const key = pendingFocus.current
    if (!key) return
    const node = gridRef.current?.querySelector<HTMLElement>(`[data-date="${key}"]`)
    if (!node) return
    pendingFocus.current = null
    node.focus()
  })

  /**
   * Whether the calendar should fold away. Only on a phone, and only once a day
   * is chosen — on a laptop there is room for both and folding would just hide
   * information for no gain.
   */
  const [narrow, setNarrow] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return

    const query = window.matchMedia('(max-width: 640px)')
    const sync = () => setNarrow(query.matches)

    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  /**
   * Tapping the folded line reopens the grid without clearing the day, so
   * changing your mind never costs you the choice you already made.
   */
  const [dayPickerOpen, setDayPickerOpen] = useState(false)
  const collapsed = narrow && selectedDate != null && !dayPickerOpen

  /**
   * On a phone the grid folds away the moment a day is picked, which would
   * drop keyboard focus on the floor. It goes to the folded line instead, which
   * names the day just chosen; the next Tab reaches the times.
   */
  useEffect(() => {
    if (!focusDaypick.current || !collapsed) return
    focusDaypick.current = false
    daypickRef.current?.focus()
  })

  const chooseDay = (cell: CalendarCell) => {
    if (isUnavailable(cell)) return
    if (narrow) focusDaypick.current = true
    setFocusedKey(cell.key)
    setDayPickerOpen(false)
    onSelectDate(cell.date)
  }

  const handleGridKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const node = (event.target as HTMLElement).closest<HTMLElement>('[data-date]')
    const key = node?.dataset.date
    if (!key) return
    const cell = cells.find((candidate) => candidate.key === key)
    if (!cell) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      chooseDay(cell)
      return
    }

    if (!isCalendarNavigationKey(event.key)) return
    event.preventDefault()

    const target = findKeyboardTarget(fromLocalDateString(key), event.key, dayIsOpen, minDate, maxDate)
    if (!target) return

    const targetKey = toLocalDateString(target)
    if (target.getFullYear() !== monthCursor.getFullYear() || target.getMonth() !== monthCursor.getMonth()) {
      onMonthChange(new Date(target.getFullYear(), target.getMonth(), 1))
    }
    setFocusedKey(targetKey)
    pendingFocus.current = targetKey
  }

  /** Times: the same single tab stop, with the arrow keys moving along the list. */
  const [focusedSlot, setFocusedSlot] = useState<string | null>(null)
  const slotStarts = slots.map((slot) => slot.startTime)
  const slotTabStop =
    (focusedSlot && slotStarts.includes(focusedSlot) ? focusedSlot : null) ??
    (selectedSlot && slotStarts.includes(selectedSlot.startTime) ? selectedSlot.startTime : null) ??
    slotStarts[0] ??
    null

  const handleSlotKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const move = SLOT_KEYS[event.key]
    if (move === undefined) return
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-slot]'))
    const current = buttons.indexOf(event.target as HTMLButtonElement)
    if (current < 0 || buttons.length === 0) return
    event.preventDefault()

    const next =
      move === 'first'
        ? 0
        : move === 'last'
          ? buttons.length - 1
          : Math.min(buttons.length - 1, Math.max(0, current + move))
    const button = buttons[next]
    setFocusedSlot(button.dataset.slot ?? null)
    button.focus()
  }

  const timesRef = useRef<HTMLElement | null>(null)
  const scrolledForDate = useRef<string | null>(null)

  /**
   * Bring the times into view when a day is picked.
   *
   * On a phone the calendar fills the panel, so the slots that appear below it
   * are entirely off-screen — the customer taps a date and nothing appears to
   * happen. Scrolling fires as soon as the section mounts rather than once the
   * slots arrive, so the loading state is visible in place instead of after a
   * pause.
   */
  useEffect(() => {
    if (!selectedDate) {
      scrolledForDate.current = null
      return
    }

    const key = toLocalDateString(selectedDate)
    // Only once per chosen day: re-scrolling as slots load or a time is picked
    // would drag the panel around under the customer.
    if (scrolledForDate.current === key) return

    const node = timesRef.current
    if (!node || typeof node.scrollIntoView !== 'function') return

    scrolledForDate.current = key

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    node.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    })
  }, [selectedDate])

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">Date &amp; time</p>
        <h3 className="ac-heading">When can we have it?</h3>
        <p className="ac-prose">Pick a day, then a drop-off time.</p>
      </div>

      <section aria-label="Choose a date" className={collapsed ? 'ac-calendar--collapsed' : undefined}>
        {/*
          Once a day is chosen the calendar folds to a single line on a phone.
          A month grid and a list of times together came to nearly two screens,
          so picking a time meant scrolling back past the thing you had already
          decided. The line stays tappable, because changing your mind is the
          only reason to come back to it.
        */}
        {collapsed ? (
          <button
            ref={daypickRef}
            type="button"
            className="ac-daypick"
            onClick={() => {
              setDayPickerOpen(true)
              pendingFocus.current = selectedKey
            }}
            aria-label={`${formatDayLabel(selectedDate)}, choose a different day`}
          >
            <span className="ac-daypick__icon" aria-hidden="true">
              <CalendarIcon size={17} />
            </span>
            <span className="ac-daypick__label">{formatDayLabel(selectedDate)}</span>
            <span className="ac-daypick__change">Change</span>
          </button>
        ) : null}

        <div className="ac-calendar__nav">
          <button
            type="button"
            className="ac-icon-btn"
            onClick={() => onMonthChange(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))}
            disabled={!canGoBack}
            aria-label="Previous month"
          >
            <ChevronLeftIcon />
          </button>
          <span className="ac-calendar__month" id={monthLabelId} aria-live="polite">
            {formatMonthLabel(monthCursor)}
          </span>
          <button
            type="button"
            className="ac-icon-btn"
            onClick={() => onMonthChange(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))}
            disabled={!canGoForward}
            aria-label="Next month"
          >
            <ChevronRightIcon />
          </button>
        </div>

        {/*
          The WAI-ARIA grid pattern: grid > row > gridcell, one row per week,
          with column headers. Each day is the focusable cell itself (no button
          inside), so Enter and Space are handled here alongside the arrows.
          Days that cannot be booked stay in the grid and say why ("closed",
          "fully booked"); the arrow keys step over them.
        */}
        <div
          ref={gridRef}
          className="ac-calendar__grid"
          role="grid"
          aria-labelledby={monthLabelId}
          aria-busy={monthLoading}
          onKeyDown={handleGridKeyDown}
        >
          <div className="ac-calendar__row" role="row">
            {WEEKDAY_LABELS.map((day, index) => (
              <div
                key={day}
                className="ac-calendar__weekday"
                role="columnheader"
                aria-label={WEEKDAY_NAMES[index]}
              >
                {day}
              </div>
            ))}
          </div>

          {toWeeks(cells).map((week) => (
            <div key={week[0].key} className="ac-calendar__row" role="row">
              {week.map((cell) => {
                if (!cell.inMonth) {
                  return <div key={cell.key} className="ac-calendar__blank" role="gridcell" />
                }

                const capacity = capacityByDate[cell.key]
                const disabled = isUnavailable(cell)
                const selected = isSameDay(selectedDate, cell.date)
                // Out of the bookable window wins over capacity: a past day the
                // month endpoint still rates "good availability" cannot be booked.
                const reason = cell.outOfRange
                  ? ', not available to book'
                  : capacity
                    ? `, ${CAPACITY_LABELS[capacity]}`
                    : ''

                return (
                  <div
                    key={cell.key}
                    role="gridcell"
                    data-date={cell.key}
                    tabIndex={cell.key === tabStopKey ? 0 : -1}
                    className={`ac-calendar__day${selected ? ' ac-calendar__day--selected' : ''}`}
                    onClick={() => chooseDay(cell)}
                    onFocus={() => setFocusedKey(cell.key)}
                    aria-label={`${formatDayLabel(cell.date)}${reason}`}
                    aria-selected={selected}
                    aria-disabled={disabled || undefined}
                  >
                    <span aria-hidden="true">{cell.date.getDate()}</span>
                    {capacity ? (
                      <span className={`ac-calendar__dot ac-calendar__dot--${capacity}`} aria-hidden="true" />
                    ) : null}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        <div className="ac-legend">
          <span className="ac-legend__item">
            <span className="ac-calendar__dot ac-calendar__dot--open" aria-hidden="true" /> Good availability
          </span>
          <span className="ac-legend__item">
            <span className="ac-calendar__dot ac-calendar__dot--limited" aria-hidden="true" /> Limited
          </span>
          <span className="ac-legend__item">
            <span className="ac-calendar__dot ac-calendar__dot--full" aria-hidden="true" /> Unavailable
          </span>
        </div>
      </section>

      {selectedDate ? (
        <section ref={timesRef} aria-label="Choose a time" className="ac-stack ac-stack--tight">
          <h4 className="ac-subheading">
            <ClockIcon size={18} /> {formatDayLabel(selectedDate)}
          </h4>

          <div aria-live="polite">
            {slotsLoading ? (
              <div className="ac-slots" aria-busy="true">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <div key={index} className="ac-skeleton" style={{ height: '58px' }} />
                ))}
              </div>
            ) : slotsError ? (
              <div className="ac-stack ac-stack--tight">
                <div className="ac-notice ac-notice--error" role="alert">
                  <AlertIcon size={18} />
                  <span>{slotsError}</span>
                </div>
                <button type="button" className="ac-btn ac-btn--secondary" onClick={onRetrySlots}>
                  Try again
                </button>
              </div>
            ) : slots.length === 0 ? (
              <div className="ac-notice ac-notice--info">
                <span>
                  No times left on {toLocalDateString(selectedDate)}. Try another day: days with a
                  green dot have the most availability.
                </span>
              </div>
            ) : (
              <div
                className="ac-slots"
                role="group"
                aria-label="Available drop-off times"
                onKeyDown={handleSlotKeyDown}
              >
                {slots.map((slot) => {
                  const selected = selectedSlot?.startTime === slot.startTime
                  return (
                    <button
                      key={slot.startTime}
                      type="button"
                      data-slot={slot.startTime}
                      tabIndex={slot.startTime === slotTabStop ? 0 : -1}
                      className={`ac-slot${selected ? ' ac-slot--selected' : ''}`}
                      onClick={() => {
                        setFocusedSlot(slot.startTime)
                        onSelectSlot(slot)
                      }}
                      aria-pressed={selected}
                    >
                      <span>{formatSlotTime(slot.startTime)}</span>
                      <span className="ac-slot__collection">
                        back {formatSlotTime(slot.endTime)}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      ) : null}
    </div>
  )
}
